import GenogramPngStreamWriter from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/export/GenogramPngStreamWriter";
import GenogramTileGridBuilder from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/export/GenogramTileGridBuilder";
import GenogramTileCaptureManager from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/export/GenogramTileCaptureManager";
import GenogramTileDecoder from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/export/GenogramTileDecoder";
import GenogramCompositeRowReader from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/export/GenogramCompositeRowReader";
import GenogramWatermarkPatternBuilder from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/export/GenogramWatermarkPatternBuilder";
import GenogramExportDiagnosticLogger from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/export/GenogramExportDiagnosticLogger";
import {
	buildGenogramOnlyExportLayout,
	buildScreenExportLayout,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/export/GenogramExportLayoutHelper";
import {
	DEFAULT_EXPORT_TILE_SIZE,
	DEFAULT_EXPORT_STRIPE_HEIGHT,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/export/GenogramExportConstants";

const TILED_EXPORT_LAYOUT_MODE = {
	SCREEN: "screen",
	GENOGRAM_ONLY: "genogram-only",
}

export default class GenogramTiledPngExportManager {

	constructor(options = {}) {
		this.logger = options.logger || new GenogramExportDiagnosticLogger()
		this.pngWriter = options.pngWriter || new GenogramPngStreamWriter({
			logger: this.logger,
		})
		this.tileGridBuilder = options.tileGridBuilder || new GenogramTileGridBuilder()
		this.tileCaptureManager = options.tileCaptureManager || new GenogramTileCaptureManager({
			logger: this.logger,
		})
		this.tileDecoder = options.tileDecoder || new GenogramTileDecoder({
			logger: this.logger,
		})
		this.watermarkPatternBuilder = options.watermarkPatternBuilder || new GenogramWatermarkPatternBuilder()
		this.stripeHeight = options.stripeHeight || DEFAULT_EXPORT_STRIPE_HEIGHT
	}

	getExportPlan(bounds, viewportSize, options = {}) {
		let tileWidth = options.tileWidth || DEFAULT_EXPORT_TILE_SIZE
		let tileHeight = this.getTileHeight(tileWidth, viewportSize)
		let plan = this.tileGridBuilder.buildGrid(bounds, {
			tileWidth,
			tileHeight,
		})
		this.logger.log("TilePlanBuilt", {
			bounds,
			planViewportWidth: viewportSize.width,
			planViewportHeight: viewportSize.height,
			tileWidth,
			tileHeight,
			tileCount: plan.tiles.length,
			finalWidth: plan.finalWidth,
			finalHeight: plan.finalHeight,
		})
		return plan
	}

	getTileHeight(tileWidth, viewportSize) {
		let viewportWidth = Math.max(viewportSize.width || 1, 1)
		let viewportHeight = Math.max(viewportSize.height || 1, 1)
		let height = Math.round(tileWidth * viewportHeight / viewportWidth)
		return Math.max(height, 1)
	}

	async buildExportOutput({
		cy,
		bounds,
		legendSource,
		showWatermark = false,
		showDebugTileBorders = false,
		onProgress = null,
		layoutMode = TILED_EXPORT_LAYOUT_MODE.SCREEN,
		maxLongSidePx = null,
		maxTotalPixels = null,
	}) {
		let startedAt = Date.now()
		let planViewportMetrics = this.getPlanViewportMetrics(cy)
		let plan = this.getExportPlan(bounds, {
			width: planViewportMetrics.viewportWidth,
			height: planViewportMetrics.viewportHeight,
		})
		this.logger.log("TiledExportStarted", {
			bounds,
			...planViewportMetrics,
			tileCount: plan.tiles.length,
			tileWidth: plan.tileWidth,
			tileHeight: plan.tileHeight,
			finalGenogramWidth: plan.finalWidth,
			finalGenogramHeight: plan.finalHeight,
			layoutMode,
			showWatermark,
			showDebugTileBorders,
		})
		let tiles = await this.tileCaptureManager.captureTiles(cy, plan, {
			onTileCaptured: onProgress,
		})
		let renderedPlan = await this.buildRenderedTileLayout(plan, tiles)
		let legendImage = await this.decodeLegendImage(legendSource)
		let exportScale = this.getExportScale(renderedPlan, {
			layoutMode,
			maxLongSidePx,
			maxTotalPixels,
		})
		let finalSize = this.getFinalCanvasSize(renderedPlan, legendImage, layoutMode, exportScale)
		this.logger.log("TiledExportLayoutReady", {
			finalWidth: finalSize.width,
			finalHeight: finalSize.height,
			genogramWidth: finalSize.genogramWidth,
			genogramHeight: finalSize.genogramHeight,
			padding: finalSize.padding,
			legendWidth: legendImage?.width || 0,
			legendHeight: legendImage?.height || 0,
			legendLeft: finalSize.legendLeft,
			legendTop: finalSize.legendTop,
			exportScale,
		})
		let rowReader = this.createCompositeRowReader(renderedPlan, renderedPlan.tiles, legendImage, finalSize, showWatermark, showDebugTileBorders)
		let blob = await this.pngWriter.buildBlob({
			width: finalSize.width,
			height: finalSize.height,
			rowReader,
		})
		let dataUrl = await this.readBlobAsDataUrl(blob)
		this.logger.log("TiledExportCompleted", {
			finalWidth: finalSize.width,
			finalHeight: finalSize.height,
			outputBlobBytes: blob.size,
			durationMs: Date.now() - startedAt,
		})

		return {
			dataUrl,
			mimeType: "image/png",
			extension: "png",
			width: finalSize.width,
			height: finalSize.height,
			blob,
		}
	}

	readBlobAsDataUrl(blob) {
		return new Promise((resolve, reject) => {
			let reader = new FileReader()
			reader.onload = () => resolve(reader.result)
			reader.onerror = () => reject(reader.error || new Error("Failed to read export blob as data URL"))
			reader.readAsDataURL(blob)
		})
	}

	getPlanViewportMetrics(cy) {
		let container = cy.container?.()
		let rect = container?.getBoundingClientRect?.() || null
		let cyViewportWidth = cy.width()
		let cyViewportHeight = cy.height()
		let containerClientWidth = container?.clientWidth || 0
		let containerClientHeight = container?.clientHeight || 0
		let containerRectWidth = Math.round(rect?.width || 0)
		let containerRectHeight = Math.round(rect?.height || 0)
		let viewportWidth = containerClientWidth || containerRectWidth || cyViewportWidth
		let viewportHeight = containerClientHeight || containerRectHeight || cyViewportHeight

		return {
			viewportWidth,
			viewportHeight,
			cyViewportWidth,
			cyViewportHeight,
			containerClientWidth,
			containerClientHeight,
			containerOffsetWidth: container?.offsetWidth || 0,
			containerOffsetHeight: container?.offsetHeight || 0,
			containerRectWidth,
			containerRectHeight,
		}
	}

	async buildRenderedTileLayout(plan, tiles) {
		let renderedTiles = await this.getRenderedTiles(tiles)
		let columnWidths = this.getMaxSizesByIndex(renderedTiles, "column", "renderedWidth")
		let rowHeights = this.getMaxSizesByIndex(renderedTiles, "row", "renderedHeight")
		let columnLefts = this.getOffsets(columnWidths)
		let rowTops = this.getOffsets(rowHeights)
		let finalWidth = this.sumSizes(columnWidths)
		let finalHeight = this.sumSizes(rowHeights)
		let positionedTiles = renderedTiles.map(tile => ({
			...tile,
			renderedLeft: columnLefts[tile.column] || 0,
			renderedTop: rowTops[tile.row] || 0,
		}))

		this.logger.log("RenderedTileLayoutBuilt", {
			plannedFinalWidth: plan.finalWidth,
			plannedFinalHeight: plan.finalHeight,
			renderedFinalWidth: finalWidth,
			renderedFinalHeight: finalHeight,
			columnCount: columnWidths.length,
			rowCount: rowHeights.length,
			columnWidths,
			rowHeights,
			tileCount: positionedTiles.length,
		})

		return {
			...plan,
			finalWidth,
			finalHeight,
			renderedColumnWidths: columnWidths,
			renderedRowHeights: rowHeights,
			tiles: positionedTiles,
		}
	}

	async getRenderedTiles(tiles) {
		let renderedTiles = []

		for (let tile of tiles) {
			let dimensions = await this.tileDecoder.getBlobDimensions(tile.blob)
			renderedTiles.push({
				...tile,
				renderedWidth: dimensions.width,
				renderedHeight: dimensions.height,
			})
		}

		return renderedTiles
	}

	getMaxSizesByIndex(items, indexField, sizeField) {
		let sizes = []

		for (let item of items) {
			let index = item[indexField]
			sizes[index] = Math.max(sizes[index] || 0, item[sizeField] || 0)
		}

		return sizes
	}

	getOffsets(sizes) {
		let offsets = []
		let currentOffset = 0

		for (let index = 0; index < sizes.length; index++) {
			offsets[index] = currentOffset
			currentOffset += sizes[index] || 0
		}

		return offsets
	}

	sumSizes(sizes) {
		let total = 0

		for (let size of sizes) {
			total += size || 0
		}

		return total
	}

	async decodeLegendImage(legendSource) {
		let legendImage = null

		if (legendSource) {
			legendImage = await this.tileDecoder.decodeSource(legendSource)
		}

		return legendImage
	}

	getExportScale(plan, options = {}) {
		let {layoutMode = TILED_EXPORT_LAYOUT_MODE.SCREEN, maxLongSidePx = null, maxTotalPixels = null} = options

		if (layoutMode !== TILED_EXPORT_LAYOUT_MODE.GENOGRAM_ONLY) {
			return 1
		}

		let width = Math.max(plan.finalWidth || 0, 1)
		let height = Math.max(plan.finalHeight || 0, 1)
		let longSide = Math.max(width, height)
		let totalPixels = width * height
		let longSideScale = maxLongSidePx ? Math.min(maxLongSidePx / longSide, 1) : 1
		let totalPixelScale = maxTotalPixels ? Math.min(Math.sqrt(maxTotalPixels / totalPixels), 1) : 1
		let scale = Math.min(longSideScale, totalPixelScale)

		return Math.max(scale, 0.01)
	}

	getFinalCanvasSize(plan, legendImage, layoutMode = TILED_EXPORT_LAYOUT_MODE.SCREEN, exportScale = 1) {
		if (layoutMode === TILED_EXPORT_LAYOUT_MODE.GENOGRAM_ONLY) {
			return buildGenogramOnlyExportLayout({
				genogramWidth: Math.max(1, Math.round(plan.finalWidth * exportScale)),
				genogramHeight: Math.max(1, Math.round(plan.finalHeight * exportScale)),
			})
		}

		return buildScreenExportLayout({
			genogramWidth: plan.finalWidth,
			genogramHeight: plan.finalHeight,
			legendWidth: legendImage?.width || 0,
			legendHeight: legendImage?.height || 0,
		})
	}

	createCompositeRowReader(plan, tiles, legendImage, finalSize, showWatermark, showDebugTileBorders) {
		let watermarkPattern = showWatermark ? this.watermarkPatternBuilder.buildPattern() : null
		let stripeHeight = Math.min(this.stripeHeight, plan.tileHeight)
		let genogramWidth = finalSize.padding === 0 ? finalSize.width : plan.finalWidth
		let genogramHeight = finalSize.padding === 0 ? finalSize.height : plan.finalHeight
		this.logger.log("RowReaderCreated", {
			stripeHeight,
			stripeRawBytes: finalSize.width * stripeHeight * 4,
			finalRawImageBytes: finalSize.width * finalSize.height * 4,
			genogramWidth,
			genogramHeight,
			watermarkPatternWidth: watermarkPattern?.width || 0,
			watermarkPatternHeight: watermarkPattern?.height || 0,
		})

		return new GenogramCompositeRowReader({
			logger: this.logger,
			finalWidth: finalSize.width,
			finalHeight: finalSize.height,
			genogramWidth,
			genogramHeight,
			sourceGenogramWidth: plan.finalWidth,
			sourceGenogramHeight: plan.finalHeight,
			padding: finalSize.padding,
			legendLeft: finalSize.legendLeft,
			legendTop: finalSize.legendTop,
			stripeHeight,
			plan,
			tiles,
			tileDecoder: this.tileDecoder,
			legendImage,
			watermarkPattern,
			showWatermark,
			showDebugTileBorders,
		})
	}

	destroy() {
		this.tileDecoder?.destroy()
	}
}
